import { format } from "date-fns";
import React from "react";
import { Form } from "react-aria-components";

import { ThemeChooserBlock, reverse, templates } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import {
    ButtonPrimary,
    ButtonSecondary,
    ButtonTertiary,
    Clickable,
    TextLink,
} from "../components/Clickables";
import { TextField, useFormSubmit } from "../components/Form";
import { H, HLevel } from "../components/Headings";
import { PageSkeleton } from "../components/PageSkeleton";
import { Svg } from "../components/Svg";
import { getThemeClass } from "../components/ThemedModule";
import { trackNewsletterSignup } from "../utils/analytics";
import { getTZShortname } from "../utils/events";
import {
    type Resource,
    type ResourceGroup,
    resources,
    resourcesIn,
} from "./GirlsProgramMaterials/resources";

import styles from "./GirlsProgramMaterials/GirlsProgramMaterials.module.scss";

type Page = templates.GirlsProgramMaterialsPageTemplate["page"];

const SUPPORT_EMAIL = "support@leaningirls.org";
const EVENTS_URL = "/events/";

const handbook = resources.find((r) => r.featured);

const STEPS: {
    theme: ThemeChooserBlock;
    title: string;
    body: string;
}[] = [
    {
        theme: "yellow",
        title: "Read the Handbook",
        body: "See how the whole program works.",
    },
    {
        theme: "pink",
        title: "Plan your term",
        body: "Use the scope and sequence to map it out.",
    },
    {
        theme: "lilac",
        title: "Run the kickoff",
        body: "Set the tone in your first session.",
    },
    {
        theme: "navy",
        title: "Facilitate parts 1–3",
        body: "Lead the full leadership journey.",
    },
];

const STATS: { value: string; label?: string }[] = [
    { value: String(resources.length), label: "free downloadable resources" },
    { value: "Ages 9–17" },
    { value: "3 parts", label: "plus kickoff & handbook" },
];

const GROUPS: {
    group: ResourceGroup;
    id?: string;
    theme: ThemeChooserBlock;
    heading: string;
    cardClass: string;
}[] = [
    {
        group: "curriculum",
        id: "curriculum",
        theme: "navy",
        heading: "Curriculum",
        cardClass: styles.cardTranslucent,
    },
    {
        group: "supporting",
        theme: "light-gray",
        heading: "Supporting resources",
        cardClass: styles.cardWhite,
    },
    {
        group: "promotional",
        theme: "yellow",
        heading: "Promotional materials",
        cardClass: styles.cardBlack,
    },
];

const ResourceAction = ({ type }: { type: Resource["type"] }) =>
    type === "SLIDES" ? (
        <span className={styles.action}>
            Open <Svg name="arrow" />
        </span>
    ) : (
        <span className={styles.action}>
            Download <Svg name="download" />
        </span>
    );

const ResourceCard = ({
    resource,
    className,
}: {
    resource: Resource;
    className: string;
}) => (
    <Clickable
        href={resource.url}
        target="_blank"
        className={concatClassNames([styles.card, className])}
    >
        <span className={styles.cardTop}>
            <span className={styles.tag}>{resource.type}</span>
            <ResourceAction type={resource.type} />
        </span>
        <H className={styles.cardTitle}>{resource.title}</H>
        {resource.description && (
            <span className={styles.cardBody}>{resource.description}</span>
        )}
    </Clickable>
);

const FeaturedCard = ({ resource }: { resource: Resource }) => (
    <Clickable
        href={resource.url}
        target="_blank"
        className={concatClassNames([
            styles.card,
            styles.featured,
            getThemeClass("poppy"),
        ])}
    >
        <span className={styles.featuredText}>
            <span className={styles.overline}>Start here</span>
            <H className={styles.featuredTitle}>{resource.title}</H>
            {resource.description && (
                <span className={styles.cardBody}>{resource.description}</span>
            )}
        </span>
        <ResourceAction type={resource.type} />
    </Clickable>
);

const ResourceGroupSection = ({
    group,
    id,
    theme,
    heading,
    cardClass,
}: (typeof GROUPS)[number]) => {
    const items = resourcesIn(group);
    const featured = items.find((r) => r.featured);
    return (
        <section
            id={id}
            aria-labelledby={`${group}-heading`}
            className={concatClassNames([
                styles.section,
                id && styles.anchorTarget,
                getThemeClass(theme),
            ])}
        >
            <div className={styles.sectionHeader}>
                <H id={`${group}-heading`} className={styles.sectionHeading}>
                    {heading}
                </H>
            </div>
            <HLevel>
                {featured && <FeaturedCard resource={featured} />}
                <ul className={styles.cards}>
                    {items
                        .filter((r) => !r.featured)
                        .map((resource) => (
                            <li key={resource.url}>
                                <ResourceCard
                                    resource={resource}
                                    className={cardClass}
                                />
                            </li>
                        ))}
                </ul>
            </HLevel>
        </section>
    );
};

const FacilitatorTraining = ({ page }: { page: Page }) => {
    const training = page.next_facilitator_training;
    const date = training?.date ? new Date(training.date) : null;
    return (
        <section aria-labelledby="training-heading" className={styles.training}>
            {page.training_image?.block && (
                <div className={styles.trainingImage}>
                    <ImageChooserBlock
                        value={page.training_image.block}
                        sizes="(max-width: 1024px) 100vw, 50vw"
                    />
                </div>
            )}
            <div
                className={concatClassNames([
                    styles.trainingPanel,
                    getThemeClass("pink"),
                ])}
            >
                <p className={styles.overline}>
                    {training ? "Upcoming event" : "Upcoming events"}
                </p>
                <H id="training-heading" className={styles.sectionHeading}>
                    Free facilitator training
                </H>
                <p className={styles.body}>
                    A 60-minute session with the Lean In Girls team on how to
                    lead a session — before you kick off your program.
                </p>
                {date && (
                    <p className={styles.trainingDate}>
                        Next session: {format(date, "eee, MMM d")} ·{" "}
                        {format(date, "h:mm a")} {getTZShortname(date)}
                    </p>
                )}
                {training?.url ? (
                    <ButtonTertiary href={training.url}>
                        View details
                    </ButtonTertiary>
                ) : (
                    <ButtonTertiary href={EVENTS_URL}>
                        See upcoming events
                    </ButtonTertiary>
                )}
            </div>
        </section>
    );
};

// Sends to the same HubSpot list as the footer signup (/api/subscribe/).
const NewsletterSignup = () => {
    const [isSuccess, setIsSuccess] = React.useState(false);
    const { isSubmitting, nonFieldError, fieldErrors, handleSubmit } =
        useFormSubmit(
            (data) =>
                fetch(reverse("newsletter-subscribe"), {
                    method: "POST",
                    body: data,
                    headers: [["Accept", "application/json"]],
                }),
            () => {
                setIsSuccess(true);
                trackNewsletterSignup("girls_program_materials");
            },
        );

    if (isSuccess) {
        return (
            <p role="status" className={styles.formMessage}>
                Thanks for signing up! You’ll hear from us soon.
            </p>
        );
    }

    return (
        <Form
            onSubmit={handleSubmit}
            validationErrors={fieldErrors}
            className={styles.form}
        >
            {nonFieldError && (
                <p role="alert" className={styles.formError}>
                    {nonFieldError}
                </p>
            )}
            <fieldset disabled={isSubmitting}>
                <TextField
                    name="email"
                    label="Email"
                    type="email"
                    isRequired
                    className={styles.input}
                />
                <ButtonPrimary type="submit">Submit</ButtonPrimary>
            </fieldset>
        </Form>
    );
};

export const Template = (
    props: templates.GirlsProgramMaterialsPageTemplate,
) => (
    <PageSkeleton {...props.page} isPreview={props.is_preview} hasVisibleH1>
        {/* 1. Hero */}
        <section
            className={concatClassNames([
                styles.hero,
                getThemeClass("light-gray"),
            ])}
        >
            <div className={styles.heroGrid}>
                <div className={styles.heroText}>
                    <p className={styles.overline}>Program Materials</p>
                    <H className={styles.heroHeading}>
                        Everything you need to run{" "}
                        <span className={styles.highlight}>Lean In Girls</span>
                    </H>
                    <div className={styles.heroButtons}>
                        {handbook && (
                            <ButtonPrimary href={handbook.url} target="_blank">
                                Download the Facilitator Handbook
                            </ButtonPrimary>
                        )}
                        <ButtonSecondary
                            href="#curriculum"
                            className={styles.heroSecondary}
                        >
                            Browse all resources
                        </ButtonSecondary>
                    </div>
                </div>
                <aside
                    aria-label="At a glance"
                    className={concatClassNames([
                        styles.glance,
                        getThemeClass("navy"),
                    ])}
                >
                    <p className={styles.overline} aria-hidden>
                        At a glance
                    </p>
                    <ul className={styles.stats}>
                        {STATS.map((stat) => (
                            <li key={stat.value}>
                                <span className={styles.statValue}>
                                    {stat.value}
                                </span>
                                {stat.label && (
                                    <span className={styles.statLabel}>
                                        {stat.label}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </aside>
            </div>
        </section>

        <HLevel>
            {/* 2. Where to start */}
            <section aria-labelledby="start-heading" className={styles.section}>
                <div className={styles.sectionHeader}>
                    <H id="start-heading" className={styles.sectionHeading}>
                        Where to start
                    </H>
                </div>
                <HLevel>
                    <ol className={styles.steps}>
                        {STEPS.map((step, i) => (
                            <li
                                key={step.title}
                                className={concatClassNames([
                                    styles.step,
                                    getThemeClass(step.theme),
                                ])}
                            >
                                <span className={styles.stepNumber} aria-hidden>
                                    {i + 1}
                                </span>
                                <H className={styles.stepTitle}>{step.title}</H>
                                <p className={styles.stepBody}>{step.body}</p>
                            </li>
                        ))}
                    </ol>
                </HLevel>
            </section>

            {/* 3–5. Curriculum, supporting and promotional resources */}
            {GROUPS.map((group) => (
                <ResourceGroupSection key={group.group} {...group} />
            ))}

            {/* 6. Facilitator training */}
            <FacilitatorTraining page={props.page} />

            {/* 7. Newsletter + contact */}
            <section
                aria-labelledby="newsletter-heading"
                className={concatClassNames([
                    styles.section,
                    styles.connect,
                    getThemeClass("navy"),
                ])}
            >
                <div className={styles.connectGrid}>
                    <div className={styles.newsletter}>
                        <H
                            id="newsletter-heading"
                            className={styles.sectionHeading}
                        >
                            Stay in the loop
                        </H>
                        <p className={styles.body}>
                            Weekly updates from the team, straight to your
                            inbox.
                        </p>
                        <NewsletterSignup />
                    </div>
                    <div className={styles.contact}>
                        <p className={styles.overline}>Questions?</p>
                        <p className={styles.contactText}>
                            We’re here to help you get started.
                        </p>
                        <TextLink
                            href={`mailto:${SUPPORT_EMAIL}`}
                            className={styles.contactLink}
                        >
                            {SUPPORT_EMAIL}
                        </TextLink>
                    </div>
                </div>
            </section>
        </HLevel>
    </PageSkeleton>
);
